
import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { FileText, Eye, ArrowUp, MessageCircle, TrendingUp, PenSquare, Settings, MoreHorizontal, Edit, Trash2, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Badge } from "@/components/ui/badge"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { useAuth } from "@/context/AuthContext"
import { formatNumber, formatRelativeTime } from "@/lib/utils"
import { getPosts, deletePost, isNetworkFetchError } from "@/lib/db"
import type { Post } from "@/types"

export default function DashboardPage() {
  const { userData, firebaseUser } = useAuth()
  const [posts, setPosts] = useState<Post[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!firebaseUser) return

    async function loadPosts() {
      try {
        const { posts: userPosts } = await getPosts({
          authorId: firebaseUser!.id,
          orderByField: "created_at",
          orderDirection: "desc",
          limitCount: 50,
        })
        setPosts(userPosts)
      } catch (error) {
        if (!isNetworkFetchError(error)) {
          console.error("Failed to load posts:", error)
        }
      } finally {
        setLoading(false)
      }
    }

    loadPosts()
  }, [firebaseUser])

  const handleDelete = async (postId: string) => {
    try {
      await deletePost(postId)
      setPosts(prev => prev.filter(p => p.id !== postId))
    } catch (error) {
      if (!isNetworkFetchError(error)) {
        console.error("Failed to delete post:", error)
      }
    }
  }

  const totalViews = posts.reduce((sum, p) => sum + (p.viewCount || 0), 0)
  const totalUpvotes = posts.reduce((sum, p) => sum + (p.upvoteCount || 0), 0)
  const totalComments = posts.reduce((sum, p) => sum + (p.commentCount || 0), 0)

  const stats = [
    { label: "Total Views", value: totalViews, icon: Eye },
    { label: "Total Upvotes", value: totalUpvotes, icon: ArrowUp },
    { label: "Comments", value: totalComments, icon: MessageCircle },
    { label: "Posts", value: posts.length, icon: FileText },
  ]

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">Welcome back, {userData?.displayName || "User"}!</p>
        </div>
        <div className="flex gap-2">
          <Link to="/settings">
            <Button variant="outline">
              <Settings className="mr-2 h-4 w-4" />
              Settings
            </Button>
          </Link>
          <Link to="/write">
            <Button>
              <PenSquare className="mr-2 h-4 w-4" />
              New Post
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map(stat => (
          <Card key={stat.label}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">{stat.label}</CardTitle>
              <stat.icon className="h-4 w-4 text-gray-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatNumber(stat.value)}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Posts */}
      <Card>
        <CardHeader>
          <CardTitle>Your Posts</CardTitle>
          <CardDescription>Manage your published and draft posts</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="all">
            <TabsList className="mb-4">
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="published">Published</TabsTrigger>
              <TabsTrigger value="drafts">Drafts</TabsTrigger>
            </TabsList>

            <TabsContent value="all" className="space-y-4">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : posts.length > 0 ? (
                posts.map(post => (
                  <div key={post.id} className="flex items-center justify-between rounded-lg border border-gray-200 p-4 dark:border-gray-800">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Link
                          to={post.status === "published" ? `/post/${post.slug}` : `/edit/${post.id}`}
                          className="font-medium text-gray-900 hover:text-indigo-600 dark:text-white dark:hover:text-indigo-400"
                        >
                          {post.title}
                        </Link>
                        <Badge variant={post.status === "published" ? "default" : "secondary"}>{post.status}</Badge>
                      </div>
                      <div className="mt-1 flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                        {post.publishedAt && <span>{formatRelativeTime(new Date(post.publishedAt))}</span>}
                        <span className="flex items-center">
                          <Eye className="mr-1 h-3 w-3" />
                          {formatNumber(post.viewCount || 0)}
                        </span>
                        <span className="flex items-center">
                          <ArrowUp className="mr-1 h-3 w-3" />
                          {formatNumber(post.upvoteCount || 0)}
                        </span>
                        <span className="flex items-center">
                          <MessageCircle className="mr-1 h-3 w-3" />
                          {formatNumber(post.commentCount || 0)}
                        </span>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <Link to={`/edit/${post.id}`}>
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem className="text-red-600" onClick={() => handleDelete(post.id)}>
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                ))
              ) : (
                <p className="py-8 text-center text-gray-500 dark:text-gray-400">No posts yet. Write your first post!</p>
              )}
            </TabsContent>

            <TabsContent value="published" className="space-y-4">
              {posts
                .filter(p => p.status === "published")
                .map(post => (
                  <div key={post.id} className="rounded-lg border border-gray-200 p-4 dark:border-gray-800">
                    <Link to={`/post/${post.slug}`} className="font-medium hover:text-indigo-600">
                      {post.title}
                    </Link>
                  </div>
                ))}
            </TabsContent>

            <TabsContent value="drafts" className="space-y-4">
              {posts
                .filter(p => p.status === "draft")
                .map(post => (
                  <div key={post.id} className="rounded-lg border border-gray-200 p-4 dark:border-gray-800">
                    <Link to={`/edit/${post.id}`} className="font-medium hover:text-indigo-600">
                      {post.title}
                    </Link>
                  </div>
                ))}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  )
}
